// oxlint-disable no-explicit-any
import {toaster} from '@cypro/ui/toaster'
import {mutators} from '@cypro/engine/mutators'
import {queries} from '@cypro/engine/queries'
import {MutateRequest} from '@rocicorp/zero'
import {useZero as useZ} from '@rocicorp/zero/react'
import {useTranslate} from '@tolgee/react'
import {useState} from 'react'

export type MutationState = 'idle' | 'pending' | 'error'

// engine validation errors arrive as English constants; show them in the user's language
const VALIDATION_ERROR_KEYS: Record<string, string> = {
  'A carrier solution is required': 'carrier_solution_required',
  'A carrier solution requires a volume greater than 0': 'volume_required',
  'A carrier solution requires an administration method': 'volume_mode_required',
  batch_in_use: 'batch_in_use_error',
  batch_nothing_to_discard: 'batch_nothing_to_discard',
  batch_discarded: 'batch_discarded_error',
}

export function useZero() {
  const z = useZ()
  const {t} = useTranslate('common')
  const [mutationState, setMutationState] = useState<MutationState>('idle')

  async function attemptMutation<T>(
    request: MutateRequest<any>,
    cb?: (result: ReturnType<typeof z.mutate>) => Promise<T>
  ) {
    setMutationState('pending')

    try {
      const mutateRes = z.mutate(request)
      const serverRes = await mutateRes.server

      try {
        // used for automation testing to listen for when a migration is done
        const event = new CustomEvent('migrationEnded', {detail: {request, result: serverRes}})
        window.dispatchEvent(event)
      } catch {}

      if (serverRes.type === 'error') throw serverRes.error

      setMutationState('idle')

      if (!cb) return

      const cbRes = await cb(mutateRes)

      return cbRes
    } catch (error) {
      setMutationState('error')
      console.error(error)

      if (error && typeof error === 'object') {
        if ('type' in error && typeof error.type === 'string' && error.type === 'zero') {
          toaster.error({title: 'Zero error', description: 'An error occurred while communicating with the server.'})
          return
        }
        if ('details' in error && typeof error.details === 'string') {
          toaster.error({title: error.details})
          return
        }

        if ('message' in error && typeof error.message === 'string') {
          // the engine's ForbiddenError carries the raw permission name; show a plain translated message
          const message = error.message.startsWith('Forbidden: missing permission') ? t('no_permission') : error.message
          const validationKey = VALIDATION_ERROR_KEYS[error.message]
          toaster.error({title: validationKey ? t(validationKey) : message})
          return
        }
      }

      if (error instanceof Error) {
        toaster.error({title: error.message})
      }
    }
  }

  return {...z, mutators, queries, attemptMutation, mutationState}
}
